import { BB_PICKUP_API_BASE } from 'lib/config/apiEndpoints';
import {
  BB_LOCATIONS_LIMIT,
  BB_ORDER_REFERENCE_KEY,
  SAMPLE_LATENCY_MS,
  isUltrahumanHost,
  sampleAvailabilityActive,
} from './config';

/** One store from the pickup API. Promise copy (`title`,
 * `ready_in_text`, `limited_quantity_text`) is composed server-side on the
 * store's own clock; render it verbatim, never rebuild dates client-side. */
export type BestBuyStore = {
  location_id: string;
  name: string;
  address: {
    line1?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  phone?: string;
  latitude?: number;
  longitude?: number;
  time_zone?: string;
  hours: BestBuyStoreHours[];
  /** Best Buy's tags for this store; one store can carry several. */
  criteria: BestBuyCriterion[];
  distance_in_miles?: number;
  pickup_date?: string;
  ready_in_text?: string;
  limited_quantity?: number;
  limited_quantity_text?: string;
  title?: string;
};

export type BestBuyStoreHours = {
  type?: string;
  days: { date: string; open?: string; close?: string }[];
};

export type BestBuyCriterion = 'BEST' | 'FASTEST' | 'NEAREST' | 'PREFERRED';

/** Which Best Buy answered: 'sandbox' is made-up test stock (pickup flag at
 * internal), 'production' is real shelves; null when pickup is off. */
export type BestBuyEnvironment = 'sandbox' | 'production' | null;

const parseEnvironment = (raw: unknown): BestBuyEnvironment =>
  raw === 'sandbox' || raw === 'production' ? raw : null;

const asTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const isCriterion = (value: unknown): value is BestBuyCriterion =>
  value === 'BEST' ||
  value === 'FASTEST' ||
  value === 'NEAREST' ||
  value === 'PREFERRED';

const parseHours = (raw: unknown): BestBuyStoreHours[] => {
  if (!Array.isArray(raw)) return [];
  const blocks: BestBuyStoreHours[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const block = entry as Record<string, unknown>;
    const days = Array.isArray(block.days)
      ? block.days.flatMap((day) => {
          if (!day || typeof day !== 'object') return [];
          const d = day as Record<string, unknown>;
          const date = asTrimmed(d.date);
          if (!date) return [];
          return [{ date, open: asTrimmed(d.open), close: asTrimmed(d.close) }];
        })
      : [];
    blocks.push({ type: asTrimmed(block.type), days });
  }
  return blocks;
};

const parseStore = (raw: unknown): BestBuyStore | null => {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const locationId = asTrimmed(s.location_id);
  const name = asTrimmed(s.name);
  if (!locationId || !name) return null;
  const address =
    s.address && typeof s.address === 'object'
      ? (s.address as Record<string, unknown>)
      : {};
  return {
    location_id: locationId,
    name,
    address: {
      line1: asTrimmed(address.line1),
      city: asTrimmed(address.city),
      region: asTrimmed(address.region),
      postal_code: asTrimmed(address.postal_code),
      country: asTrimmed(address.country),
    },
    phone: asTrimmed(s.phone),
    latitude: asNumber(s.latitude),
    longitude: asNumber(s.longitude),
    time_zone: asTrimmed(s.time_zone),
    hours: parseHours(s.hours),
    criteria: isCriterion(s.criterion) ? [s.criterion] : [],
    distance_in_miles: asNumber(s.distance_in_miles),
    pickup_date: asTrimmed(s.pickup_date),
    ready_in_text: asTrimmed(s.ready_in_text),
    limited_quantity: asNumber(s.limited_quantity),
    limited_quantity_text: asTrimmed(s.limited_quantity_text),
    title: asTrimmed(s.title),
  };
};

/** Every live call is capped: a hung connection must not leave the buy page
 * "checking" or the checkout spinner running. The proxies use the same
 * numbers. The caller's own signal still aborts a stale request, and that
 * abort is told apart from the cap so callers can ignore it as before. */
const REQUEST_TIMEOUT_MS = 10_000;
const HOLD_TIMEOUT_MS = 15_000;

type CappedSignal = {
  signal: AbortSignal;
  timedOut: () => boolean;
  done: () => void;
};

const withTimeout = (
  parent: AbortSignal | undefined,
  ms: number,
): CappedSignal => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, ms);
  const onParent = () => controller.abort();
  if (parent?.aborted) controller.abort();
  else parent?.addEventListener('abort', onParent, { once: true });
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    done: () => {
      clearTimeout(timer);
      parent?.removeEventListener('abort', onParent);
    },
  };
};

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

/** Fetch's abort contract for the sample path: reject promptly on abort,
 * never leave the timer running. */
const abortableDelay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const abortError = () => new DOMException('Aborted', 'AbortError');
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

// ---------------------------------------------------------------------------
// Buy-page pickup: GET /stores, POST /holds, DELETE /holds/:order_reference
// ---------------------------------------------------------------------------

/** One store from /stores: a BestBuyStore plus the
 * per-SKU stock answer. `title` is server copy ("Pick up today at Best Buy
 * Richfield"): render it verbatim. */
export type BestBuyPickupStore = BestBuyStore & {
  allItemsAvailable: boolean;
  hasLimitedQuantity: boolean;
  availabilityText?: string;
  /** Short-lived. Never keep one: refetch right before a hold. */
  availabilityTokens: string[];
  items: BestBuyPickupItem[];
};

export type BestBuyPickupItem = {
  sku: string;
  quantity: number;
  availabilityDate?: string;
  limitedQuantity?: number;
  limitedQuantityText?: string;
  availabilityToken?: string;
};

export type BestBuyStoresData = {
  pickupAvailable: boolean;
  postalCode: string;
  environment: BestBuyEnvironment;
  /** Complete stores first, as the server ordered them. */
  stores: BestBuyPickupStore[];
};

export type BestBuyHold = {
  orderReference: string;
  /** ISO instant, about 15 minutes after creation. */
  expiresAt: string;
  locationId: string;
  sku: string;
  promiseDate?: string;
};

export type BestBuyFailReason =
  | 'invalid_zip'
  | 'bad_request'
  | 'rate_limited'
  | 'unprocessable'
  | 'unreachable'
  | 'not_found'
  | 'conflict'
  | 'http_error'
  | 'bad_response'
  | 'network';

export type BestBuyFail = {
  ok: false;
  reason: BestBuyFailReason;
  status: number;
  code: string;
  /** Server copy, ready to show; empty when the server sent none. */
  message: string;
};

export type BestBuyApiResult<T> = { ok: true; data: T } | BestBuyFail;

const PICKUP_API_PATH = '/api/web_v1/ring/best_buy_pickup';
const PICKUP_PROXY_PATH = '/api/ring/best-buy-pickup';

const parseStatus = (status: number, zipLookup: boolean): BestBuyFailReason => {
  if (status === 400) return zipLookup ? 'invalid_zip' : 'bad_request';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'unprocessable';
  if (status === 429) return 'rate_limited';
  if (status === 503) return 'unreachable';
  return 'http_error';
};

const failFrom = (
  status: number,
  body: unknown,
  zipLookup: boolean,
): BestBuyFail => {
  const err = (body ?? {}) as {
    error?: { code?: string | number; message?: string } | string;
  };
  const detail = typeof err.error === 'object' ? err.error : undefined;
  const rawCode = detail?.code;
  return {
    ok: false,
    reason: parseStatus(status, zipLookup),
    status,
    code:
      typeof rawCode === 'number' ? String(rawCode) : asTrimmed(rawCode) ?? '',
    message:
      asTrimmed(detail?.message) ??
      (typeof err.error === 'string' ? err.error : ''),
  };
};

/** Direct on ultrahuman.com origins, same-origin proxy elsewhere (trailing
 * slash: the site 308s without it). `path` is relative, e.g. "stores". */
const pickupUrl = (path: string, query = ''): string =>
  isUltrahumanHost()
    ? `${BB_PICKUP_API_BASE}${PICKUP_API_PATH}/${path}${query}`
    : `${PICKUP_PROXY_PATH}/${path}/${query}`;

const requestPickup = async <T>(args: {
  path: string;
  query?: string;
  method?: 'GET' | 'POST' | 'DELETE';
  json?: unknown;
  signal?: AbortSignal;
  zipLookup?: boolean;
  /** Also accept a body that is not wrapped in `{status, data}`. */
  acceptBare?: boolean;
  parse: (data: unknown) => T | null;
}): Promise<BestBuyApiResult<T>> => {
  const {
    path,
    query,
    method = 'GET',
    json,
    signal,
    zipLookup,
    acceptBare,
    parse,
  } = args;
  const cap = withTimeout(
    signal,
    method === 'POST' ? HOLD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
  );
  let response: Response;
  let body: unknown = null;
  try {
    response = await fetch(pickupUrl(path, query), {
      method,
      signal: cap.signal,
      ...(json !== undefined
        ? {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(json),
          }
        : {}),
    });
    try {
      body = await response.json();
    } catch (error) {
      if (cap.signal.aborted) throw error;
      body = null;
    }
  } catch (error) {
    // A stale request the caller aborted: still thrown, as before.
    if (!cap.timedOut() && isAbort(error)) throw error;
    // The cap fired, or the network failed: a result the caller shows
    // ("We couldn't reach the store"), never a request left hanging.
    const timedOut = cap.timedOut();
    return {
      ok: false,
      reason: timedOut ? 'unreachable' : 'network',
      status: 0,
      code: timedOut ? 'timeout' : 'network',
      message: '',
    };
  } finally {
    cap.done();
  }
  if (!response.ok) return failFrom(response.status, body, Boolean(zipLookup));
  const envelope = body as { status?: string; data?: unknown } | null;
  const data =
    envelope?.status === 'ok'
      ? parse(envelope.data)
      : acceptBare
      ? parse(body)
      : null;
  if (data === null) {
    return {
      ok: false,
      reason: 'bad_response',
      status: response.status,
      code: 'bad_response',
      message: '',
    };
  }
  return { ok: true, data };
};

const parsePickupItem = (raw: unknown): BestBuyPickupItem | null => {
  if (!raw || typeof raw !== 'object') return null;
  const i = raw as Record<string, unknown>;
  const sku = asTrimmed(i.sku);
  if (!sku) return null;
  return {
    sku,
    quantity: asNumber(i.quantity) ?? 1,
    availabilityDate: asTrimmed(i.availability_date),
    limitedQuantity: asNumber(i.limited_quantity),
    limitedQuantityText: asTrimmed(i.limited_quantity_text),
    availabilityToken: asTrimmed(i.availability_token),
  };
};

const parsePickupStore = (raw: unknown): BestBuyPickupStore | null => {
  const base = parseStore(raw);
  if (!base) return null;
  const s = raw as Record<string, unknown>;
  return {
    ...base,
    allItemsAvailable: s.all_items_available === true,
    hasLimitedQuantity: s.has_limited_quantity === true,
    availabilityText: asTrimmed(s.availability_text),
    availabilityTokens: Array.isArray(s.availability_tokens)
      ? s.availability_tokens.flatMap((token) => {
          const trimmed = asTrimmed(token);
          return trimmed ? [trimmed] : [];
        })
      : [],
    items: Array.isArray(s.items)
      ? s.items.flatMap((item) => {
          const parsed = parsePickupItem(item);
          return parsed ? [parsed] : [];
        })
      : [],
  };
};

const parseStoresData = (raw: unknown): BestBuyStoresData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const postalCode = asTrimmed(d.postal_code);
  if (!postalCode) return null;
  return {
    pickupAvailable: d.pickup_available === true,
    postalCode,
    environment: parseEnvironment(d.environment),
    // Server order is kept: complete stores come first.
    stores: Array.isArray(d.stores)
      ? d.stores.flatMap((entry) => {
          const store = parsePickupStore(entry);
          return store ? [store] : [];
        })
      : [],
  };
};

const parseHold = (raw: unknown): BestBuyHold | null => {
  if (!raw || typeof raw !== 'object') return null;
  const h = raw as Record<string, unknown>;
  const orderReference = asTrimmed(h.order_reference);
  const expiresAt = asTrimmed(h.expires_at);
  const locationId = asTrimmed(h.location_id);
  const sku = asTrimmed(h.sku);
  if (!orderReference || !expiresAt || !locationId || !sku) return null;
  if (Number.isNaN(new Date(expiresAt).getTime())) return null;
  // The paid order is joined to its hold through the note attribute the
  // backend names. The cart only accepts BB_ORDER_REFERENCE_KEY, so a hold
  // that names another key could never be joined: refuse it.
  const noteAttribute = asTrimmed(h.note_attribute);
  if (noteAttribute && noteAttribute !== BB_ORDER_REFERENCE_KEY) return null;
  return {
    orderReference,
    expiresAt,
    locationId,
    sku,
    promiseDate: asTrimmed(h.promise_date),
  };
};

export type BestBuyStoresItem = { sku: string; quantity: number };

/** GET /stores: which stores can hand over these exact SKUs, with a fresh
 * availability token per item. Never cached by the server; do not cache. */
export async function fetchBestBuyStores(args: {
  postalCode: string;
  items: BestBuyStoresItem[];
  signal?: AbortSignal;
}): Promise<BestBuyApiResult<BestBuyStoresData>> {
  const { postalCode, items, signal } = args;

  if (sampleAvailabilityActive()) {
    const [{ sampleStoresBody }] = await Promise.all([
      import('./sampleData'),
      abortableDelay(SAMPLE_LATENCY_MS, signal),
    ]);
    if (postalCode === '00000') {
      return {
        ok: false,
        reason: 'invalid_zip',
        status: 400,
        code: 'invalid_zip',
        message: '',
      };
    }
    if (postalCode === '00001') {
      return {
        ok: false,
        reason: 'http_error',
        status: 500,
        code: 'http_error',
        message: '',
      };
    }
    const data = parseStoresData(sampleStoresBody(postalCode, items).data);
    return data
      ? { ok: true, data }
      : {
          ok: false,
          reason: 'bad_response',
          status: 200,
          code: 'bad_response',
          message: '',
        };
  }

  // Rails-style repeated keys: items[][sku]=A&items[][quantity]=1&...
  const params = new URLSearchParams({ postal_code: postalCode });
  for (const item of items) {
    params.append('items[][sku]', item.sku);
    params.append('items[][quantity]', String(item.quantity));
  }
  return requestPickup({
    path: 'stores',
    query: `?${params.toString()}`,
    signal,
    zipLookup: true,
    parse: parseStoresData,
  });
}

// ---------------------------------------------------------------------------
// Homepage finder: GET /locations (the store directory around a ZIP or point)
// ---------------------------------------------------------------------------

export type BestBuyLocationsStatus =
  | 'off'
  | 'not_found'
  | 'none_nearby'
  | 'building'
  | 'ok';

export type BestBuyLocationsData = {
  pickupAvailable: boolean;
  status: BestBuyLocationsStatus;
  environment: BestBuyEnvironment;
  /** The ZIP the lookup resolved to (typed, or reverse-geocoded by the API from
   * coordinates); null when the point is outside the US. */
  postalCode: string | null;
  /** Nearest first, as the server ordered them. */
  stores: BestBuyStore[];
};

export type BestBuyLocationsQuery =
  | { postalCode: string }
  | { latitude: number; longitude: number };

const US_ZIP_PLUS4_RE = /^\d{5}(-\d{4})?$/;

const LOCATIONS_STATUSES: readonly string[] = [
  'off',
  'not_found',
  'none_nearby',
  'building',
  'ok',
] satisfies BestBuyLocationsStatus[];

const isLocationsStatus = (value: unknown): value is BestBuyLocationsStatus =>
  typeof value === 'string' && LOCATIONS_STATUSES.includes(value);

const parseLocationsData = (raw: unknown): BestBuyLocationsData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (!isLocationsStatus(d.status)) return null;
  const postalCode = asTrimmed(d.postal_code);
  return {
    pickupAvailable: d.pickup_available === true,
    status: d.status,
    environment: parseEnvironment(d.environment),
    // Five digits; a ZIP+4 from the reverse geocode is cut to its ZIP.
    postalCode:
      postalCode && US_ZIP_PLUS4_RE.test(postalCode)
        ? postalCode.slice(0, 5)
        : null,
    stores: Array.isArray(d.stores)
      ? d.stores.flatMap((entry) => {
          const store = parseStore(entry);
          return store ? [store] : [];
        })
      : [],
  };
};

/** GET /locations: the Best Buy stores around a ZIP or a point. No stock. */
export async function fetchBestBuyLocations(args: {
  query: BestBuyLocationsQuery;
  signal?: AbortSignal;
}): Promise<BestBuyApiResult<BestBuyLocationsData>> {
  const { query, signal } = args;
  const zipLookup = 'postalCode' in query;

  if (sampleAvailabilityActive()) {
    const [{ sampleLocationsBody }] = await Promise.all([
      import('./sampleData'),
      abortableDelay(SAMPLE_LATENCY_MS, signal),
    ]);
    if (zipLookup && query.postalCode === '00001') {
      return {
        ok: false,
        reason: 'http_error',
        status: 500,
        code: 'http_error',
        message: '',
      };
    }
    const data = parseLocationsData(sampleLocationsBody(query).data);
    return data
      ? { ok: true, data }
      : {
          ok: false,
          reason: 'bad_response',
          status: 200,
          code: 'bad_response',
          message: '',
        };
  }

  const params = new URLSearchParams(
    zipLookup
      ? { postal_code: query.postalCode }
      : {
          latitude: query.latitude.toFixed(2),
          longitude: query.longitude.toFixed(2),
        },
  );
  params.set('limit', String(BB_LOCATIONS_LIMIT));
  // A 400 is about the ZIP only when a ZIP was sent; a coordinates 400 is a
  // plain request error, never a field error on an empty ZIP box.
  return requestPickup({
    path: 'locations',
    query: `?${params.toString()}`,
    signal,
    zipLookup,
    parse: parseLocationsData,
  });
}

/** POST /holds: reserves the unit for about 15 minutes. */
export async function createBestBuyHold(args: {
  sku: string;
  locationId: string;
  availabilityToken: string;
  signal?: AbortSignal;
}): Promise<BestBuyApiResult<BestBuyHold>> {
  const { sku, locationId, availabilityToken, signal } = args;
  if (sampleAvailabilityActive()) {
    const [{ sampleHoldBody }] = await Promise.all([
      import('./sampleData'),
      abortableDelay(SAMPLE_LATENCY_MS, signal),
    ]);
    const data = parseHold(sampleHoldBody(sku, locationId).data);
    return data
      ? { ok: true, data }
      : {
          ok: false,
          reason: 'bad_response',
          status: 201,
          code: 'bad_response',
          message: '',
        };
  }
  return requestPickup({
    path: 'holds',
    method: 'POST',
    json: {
      sku,
      location_id: locationId,
      availability_token: availabilityToken,
    },
    signal,
    parse: parseHold,
  });
}

/** DELETE /holds/:order_reference. Best effort: callers never wait on it. */
export async function releaseBestBuyHold(
  orderReference: string,
  signal?: AbortSignal,
): Promise<BestBuyApiResult<{ released: boolean }>> {
  if (sampleAvailabilityActive()) {
    return { ok: true, data: { released: true } };
  }
  return requestPickup({
    path: `holds/${encodeURIComponent(orderReference)}`,
    method: 'DELETE',
    signal,
    parse: (data) => {
      if (!data || typeof data !== 'object') return null;
      return {
        released: (data as Record<string, unknown>).released === true,
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Catalogue: GET /catalogue (which colour + size pairs Best Buy stocks)
// ---------------------------------------------------------------------------

export type BestBuyCatalogueItem = {
  /** Ops SKU, the one web sends (ringProOpsSkuCode), e.g. "PG08". */
  sku: string;
  color: string;
  size: number;
  /** Shopify Best Buy variant; null until the backend links it (27 Oct:
   * null on every item). Eligibility only needs `sku`. */
  variantId: string | null;
  bestBuySku: string | null;
};

export type BestBuyCatalogueData = {
  pickupAvailable: boolean;
  environment: BestBuyEnvironment;
  /** Only the pickup-eligible combinations. Global per SKU, not per store. */
  items: BestBuyCatalogueItem[];
};

const CATALOGUE_SKU_RE = /^[A-Za-z0-9-]{1,20}$/;

/** A number or numeric string (variant ids are strings, but be lenient). */
const asId = (value: unknown): string | undefined =>
  typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : asTrimmed(value);

const parseCatalogueItem = (raw: unknown): BestBuyCatalogueItem | null => {
  if (!raw || typeof raw !== 'object') return null;
  const i = raw as Record<string, unknown>;
  const sku = asTrimmed(i.sku);
  const color = asTrimmed(i.color);
  const size = asNumber(i.size);
  const variantId = asId(i.variant_id);
  const bestBuySku = asId(i.best_buy_sku);
  if (!sku || !CATALOGUE_SKU_RE.test(sku)) return null;
  if (!color || size === undefined) return null;
  return {
    sku,
    color,
    size,
    variantId: variantId ?? null,
    bestBuySku: bestBuySku ?? null,
  };
};

/** Accepts the bare body and the `{status, data}` envelope alike. */
const parseCatalogueData = (raw: unknown): BestBuyCatalogueData | null => {
  if (!raw || typeof raw !== 'object') return null;
  let d = raw as Record<string, unknown>;
  if (
    d.pickup_available === undefined &&
    d.items === undefined &&
    d.data &&
    typeof d.data === 'object'
  ) {
    d = d.data as Record<string, unknown>;
  }
  if (typeof d.pickup_available !== 'boolean' || !Array.isArray(d.items)) {
    return null;
  }
  return {
    pickupAvailable: d.pickup_available,
    environment: parseEnvironment(d.environment),
    items: d.items.flatMap((entry) => {
      const item = parseCatalogueItem(entry);
      return item ? [item] : [];
    }),
  };
};

/** GET /catalogue: the pickup-eligible colour + size pairs. Not per store or
 * ZIP, so one fetch per page session is enough. */
export async function fetchBestBuyCatalogue(
  args: {
    signal?: AbortSignal;
  } = {},
): Promise<BestBuyApiResult<BestBuyCatalogueData>> {
  // Sample mode has no catalogue, so pickup is off there: the buy page shows
  // the greyed-out "off" state (there is no hardcoded fallback any more).
  if (sampleAvailabilityActive()) {
    return {
      ok: false,
      reason: 'not_found',
      status: 404,
      code: 'sample',
      message: '',
    };
  }
  return requestPickup({
    path: 'catalogue',
    signal: args.signal,
    acceptBare: true,
    parse: parseCatalogueData,
  });
}
